import {useRequiredConfig} from '@domain/shifthandover'
import {Spot} from '@domain/shifthandover/src/events/types'
import {
  CategoryDropDown,
  ProcessStageDropDown,
  EventProcessStageAutocomplete,
  PriorityDropDown
} from '@hconnect/common/components/shiftEventFormFields'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {REGULAR_SIZE, INPUT_FORM_FIELD_SX} from '@hconnect/common/consts'
import {EquipmentIdType, EventType, BaseEvent} from '@hconnect/common/types'
import {Grid, TextField, Box} from '@mui/material'
import {EventDescriptionBox} from '@widgets/common-shift-event'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {EquipmentSearchContainer, SpotInspectionDetails} from '../eventProperties'
import {EventInfo} from '../eventProperties/EventInfo'
import {EventStatusDropDown} from '../eventProperties/EventStatus'
import {EventTypeDropdownBox} from '../eventProperties/EventTypeDropdownBox'

type PossibleErrors = keyof Spot | 'equipmentNumber'

type Props = {
  plantId: string
  item: Partial<Spot>
  update: <K extends keyof Spot>(key: K, value: Spot[K] | EventType) => void
  updateMultiPart: (delta: Partial<BaseEvent>) => void
  validationError: Map<PossibleErrors, string>
  children: React.ReactNode
  equipmentIdType?: EquipmentIdType
  autocompleteProcessStage?: boolean
}

export const SpotEventForm: React.FC<Props> = ({
  plantId,
  children,
  item,
  update,
  updateMultiPart,
  validationError,
  equipmentIdType,
  autocompleteProcessStage
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const getErrorText = (key: PossibleErrors) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const {workOrderTarget} = useRequiredConfig(plantId)

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          data-test-id="event-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('shiftEvent.label.title')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update('title', event.target.value)
          }
          required
          value={item.title}
          variant="filled"
          inputProps={{'data-test-id': 'event-form-title-input'}}
          InputProps={INPUT_FORM_FIELD_SX}
        />
        <EventHighlight
          title={t('eventsTable.action.highlight')}
          value={item.isHighlighted}
          onChange={(value) => update('isHighlighted', value)}
        />
      </Box>

      {'created' in item && (
        <EventInfo
          item={item as Partial<BaseEvent>}
          plantId={plantId}
          workOrderTarget={workOrderTarget}
        />
      )}

      <Box my={1}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item {...REGULAR_SIZE}>
            <EventTypeDropdownBox
              item={item}
              value="spot"
              disabled
              additionalOptions={['spot']}
              updateMultiPart={updateMultiPart}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE}>
            <EventStatusDropDown
              translate={t}
              data-test-id="event-form-status"
              errorText={getErrorText('status')}
              onChange={(value) => update('status', value)}
              value={item.status}
            />
          </Grid>

          <Grid item xs={12}>
            <EquipmentSearchContainer
              plantId={plantId}
              data-test-id="event-form-equipmentNumber"
              errorText={getErrorText('equipmentNumber')}
              updateMultiPart={updateMultiPart}
              equipmentIdType={equipmentIdType}
              value={item as BaseEvent}
            />
          </Grid>

          {autocompleteProcessStage ? (
            <Grid item xs={12}>
              <EventProcessStageAutocomplete
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value?: string) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          ) : (
            <Grid item {...REGULAR_SIZE}>
              <ProcessStageDropDown
                translate={t}
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          )}
          <Grid item {...REGULAR_SIZE}>
            <CategoryDropDown
              translate={t}
              data-test-id="event-form-category"
              errorText={getErrorText('category')}
              onChange={(value) => update('category', value)}
              value={item.category}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE}>
            <PriorityDropDown
              translate={t}
              data-test-id="event-form-priority"
              errorText={getErrorText('priority')}
              onChange={(value) => update('priority', value)}
              value={item.priority}
            />
          </Grid>

          {!!item.spotInspections?.length && (
            <Grid item xs={12}>
              <SpotInspectionDetails spotInspections={item.spotInspections} />
            </Grid>
          )}

          <Grid item xs={12}>
            <EventDescriptionBox
              current={item.description || ''}
              errorCode={validationError.get('description')}
              onChange={(next) => update('description', next)}
              required
            />
          </Grid>
        </Grid>
      </Box>
      {children}
    </>
  )
}
